import { useState, useEffect, useCallback } from "react";
import { isOctopusConfigured, pendingCount, syncWithPi, uploadAllPending } from "../lib/octopus";
import PiHelp from "./PiHelp";

/**
 * How long the Pi has to be unreachable before the chip offers help. Long enough that a
 * cable going in (green within a second) never shows it; short enough to catch a scout
 * whose laptop never sees the cable at all - which no retry can fix.
 */
export const HELP_AFTER_MS = 10000;

function hostOf(server) {
    try {
        return new URL(server).hostname;
    } catch {
        return server;
    }
}

/**
 * Is the Octopus server on the Pi reachable, and how much is still owed to it?
 *
 * Polls `/health`, which never needs anything to already work, so "the Pi is down" stays
 * distinguishable from "the Pi refuses this laptop's records" (event closed or not
 * registered) — the one confusion worth avoiding at a venue. The second shows amber.
 *
 * @param {int} interval how often to re-check, ms
 * @param {String} eventPath which event's queue to count
 */
/**
 * Send waiting records to the Pi on every screen, not just where the status chip is drawn.
 * Mounted once in App.js: a match saved while the Pi was unplugged goes up within one interval
 * of the Pi coming back, even while the scout is mid-match. No React state, so nothing
 * re-renders; syncWithPi runs one sync at a time, so it never doubles up with the chip's.
 */
export function usePiBackgroundSync(eventPath = "", interval = 10000) {
    useEffect(() => {
        if (!eventPath) return undefined;
        return checkOnRepeat(async () => {
            if (!isOctopusConfigured() || pendingCount(eventPath) === 0) return true;
            return (await syncWithPi(eventPath)).reachable;
        }, interval);
    }, [eventPath, interval]);
}

/**
 * How soon to ask again while the Pi is unreachable. Plugging the cable back in while
 * Wi-Fi stays connected fires no browser event, so this check is what notices it - within
 * about a second, however long the cable was out (each check also re-asks every second while
 * it waits, lib/octopus.js HEALTH_RESEND). Once the Pi answers, the normal interval resumes.
 */
export const OFFLINE_RETRY_MS = 1000;

/**
 * Run `check` now and again after each answer: `interval` later when it returned true (the
 * Pi answered), OFFLINE_RETRY_MS later when it did not. Each check starts after the last
 * one finishes, so a check timing out never piles up behind another. Also runs at once
 * when the browser says the network changed or the laptop woke. Returns the cleanup.
 */
function checkOnRepeat(check, interval) {
    let stopped = false;
    let timer = null;
    const run = async () => {
        clearTimeout(timer);
        // A check that throws counts as "not answered": the loop must never stop, or the
        // chip freezes and no reconnect is ever noticed.
        let answered = false;
        try {
            answered = await check();
        } catch {}
        if (stopped) return;
        clearTimeout(timer);
        timer = setTimeout(run, answered === false ? OFFLINE_RETRY_MS : interval);
    };
    const onVisible = () => {
        if (document.visibilityState !== "hidden") run();
    };
    const connection = typeof navigator !== "undefined" ? navigator.connection : null;

    run();
    window.addEventListener("online", run);
    document.addEventListener("visibilitychange", onVisible);
    connection?.addEventListener?.("change", run);
    return () => {
        stopped = true;
        clearTimeout(timer);
        window.removeEventListener("online", run);
        document.removeEventListener("visibilitychange", onVisible);
        connection?.removeEventListener?.("change", run);
    };
}

export function useOctopusStatus(interval = 10000, eventPath = "") {
    const [state, setState] = useState({
        configured: isOctopusConfigured(),
        reachable: false,
        reason: "checking",
        refused: null,
        pending: 0,
    });

    const check = useCallback(async () => {
        if (!isOctopusConfigured()) {
            // Scouting and Timekeeper records together: the scout only needs to know
            // whether anything is still waiting, not which stream it is in.
            setState({ configured: false, reachable: false, reason: "turned off in Settings", refused: null, pending: pendingCount(eventPath) });
            return true; // nothing to look for, so nothing to retry
        }
        // Checks the Pi and, if it answers, sends anything waiting - so a record whose
        // first send failed goes up within one interval, with no tap needed. The count
        // shown is taken after sending, so a record that just went up is not shown as
        // still pending.
        const status = await syncWithPi(eventPath);
        setState({ configured: true, ...status });
        return status.reachable;
    }, [eventPath]);

    // Every `interval` while connected, every OFFLINE_RETRY_MS while not, and at once when
    // the browser fires "online" or the laptop wakes. Overlapping checks are harmless -
    // syncWithPi runs one at a time and hands a second caller the same answer.
    useEffect(() => checkOnRepeat(check, interval), [check, interval]);

    return { ...state, refresh: check };
}

/**
 * The indicator itself. Four states, because they need different actions from a
 * scout: no server configured (go to Settings), configured but unreachable (check the
 * cable or the Pi), reachable but refusing this laptop's records (check the event on
 * Home, or the Pi), and connected (with anything still waiting to go up).
 */
function OctopusStatus({ eventPath = "", interval = 10000 }) {
    const { configured, reachable, reason, refused, blocked, pending, server, refresh } = useOctopusStatus(interval, eventPath);
    // "via pi.db-hub.me" (DNS) or "via 10.42.0.1" (no DNS) - which of the Pi's addresses answered.
    const via = reachable && server ? ` via ${hostOf(server)}` : "";
    const offline = configured && !reachable;
    const [helpReady, setHelpReady] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    const closeHelp = useCallback(() => setHelpOpen(false), []);

    // Offer help once the Pi has been unreachable for HELP_AFTER_MS without a break.
    useEffect(() => {
        if (!offline) {
            setHelpReady(false);
            return undefined;
        }
        const timer = setTimeout(() => setHelpReady(true), HELP_AFTER_MS);
        return () => clearTimeout(timer);
    }, [offline]);

    // "blocked": the browser refuses this site the local network, so no cable will help -
    // the scout has to allow it from the address bar's site settings.
    const color = !configured ? "var(--gray, #888)" : !reachable ? "#ef4444" : refused ? "#f59e0b" : "#22c55e";
    const label = !configured ? "Pi: off" : blocked ? "Pi: allow local network" : !reachable ? "Pi: offline" : refused ? "Pi: check event" : "Pi";

    // Waiting records already go up on their own at every check (syncWithPi). Tapping
    // just does it now, without waiting for the next interval.
    // While it can't be reached, a tap also opens what to check on this laptop.
    const retry = async () => {
        if (!configured) return;
        if (offline) setHelpOpen(true);
        await uploadAllPending(eventPath).catch(() => {});
        refresh();
    };

    return (
        <>
        <div
            onClick={retry}
            title={configured ? `Octopus server — ${reason}${via}. Tap to ${offline ? "see what to check" : "retry"}` : "Octopus server turned off (Settings)"}
            style={{
                display: "flex",
                alignItems: "center",
                gap: "0.35rem",
                fontSize: "0.8rem",
                cursor: configured ? "pointer" : "default",
            }}
        >
            <span
                aria-hidden="true"
                style={{
                    width: "0.6rem",
                    height: "0.6rem",
                    borderRadius: "50%",
                    background: color,
                    flexShrink: 0,
                }}
            />
            <span>
                {label}
                {configured && pending > 0 ? ` (${pending})` : ""}
                {offline && helpReady ? " · help" : ""}
            </span>
        </div>
        {helpOpen && <PiHelp reachable={reachable} blocked={blocked} onClose={closeHelp} />}
        </>
    );
}

export default OctopusStatus;
